import { useState, useRef, useEffect } from 'react';
import { format, addDays, startOfWeek } from 'date-fns';
import { he } from 'date-fns/locale';
import { WeeklyTask } from '@/hooks/useWeeklyTasks';
import { MobileCalendarHeader } from './MobileCalendarHeader';
import { MobileDayView } from './MobileDayView';
import { UnscheduledTasksDrawer } from './UnscheduledTasksDrawer';

interface MobileCalendarViewProps {
  weekOffset: number;
  currentTime: Date;
  selectedUsers: string[];
  weeklyTasks: WeeklyTask[];
  googleEvents: any[];
  matanBalance: { balance: number } | undefined;
  isUserAdmin: boolean;
  onChangeWeek: (direction: number) => void;
  onGoToToday: () => void;
  onGoToDate: (date: Date) => void;
  onToggleUser: (userId: string) => void;
  onOpenUserSettings: (userId: string) => void;
  onOpenManagement: () => void;
  onOpenAddEvent: () => void;
  onOpenCoinHistory: () => void;
  onTransferCoins: () => void;
  onEditTask: (task: WeeklyTask) => void;
  onNavigateToTasks: () => void;
  transferCoinsPending: boolean;
}

const USERS = [
  { id: 'matan', name: 'מתן', color: 'hsl(var(--skylight-blue))', textColor: 'text-skylight-blue' },
  { id: 'heila', name: 'הילה', color: 'hsl(var(--skylight-pink))', textColor: 'text-skylight-pink' },
  { id: 'asaf', name: 'אסף', color: 'hsl(var(--skylight-green))', textColor: 'text-skylight-green' }
];

const HOURS = Array.from({ length: 17 }, (_, i) => i + 7);
const EN_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const getEnglishDayKey = (date: Date) => EN_DAYS[date.getDay()];

const normalizeDay = (value?: string | null) => {
  if (!value) return null;
  const heToEn: Record<string, string> = {
    'ראשון': 'Sunday',
    'שני': 'Monday',
    'שלישי': 'Tuesday',
    'רביעי': 'Wednesday',
    'חמישי': 'Thursday',
    'שישי': 'Friday',
    'שבת': 'Saturday',
  };
  return heToEn[value] || value;
};

export function MobileCalendarView({
  weekOffset,
  currentTime,
  selectedUsers,
  weeklyTasks,
  googleEvents,
  matanBalance,
  isUserAdmin,
  onChangeWeek,
  onGoToToday,
  onGoToDate,
  onToggleUser,
  onOpenUserSettings,
  onOpenManagement,
  onOpenAddEvent,
  onOpenCoinHistory,
  onTransferCoins,
  onEditTask,
  onNavigateToTasks,
  transferCoinsPending
}: MobileCalendarViewProps) {
  // Start with today's day index (0-6)
  const todayDayIndex = new Date().getDay();
  const [selectedDayIndex, setSelectedDayIndex] = useState(weekOffset === 0 ? todayDayIndex : 0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 });
  const currentWeekStart = addDays(weekStart, weekOffset * 7);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  const selectedDay = weekDays[selectedDayIndex];

  // Reset to Sunday when changing weeks (except current week -> go to today)
  useEffect(() => {
    if (weekOffset === 0) {
      setSelectedDayIndex(new Date().getDay());
    } else {
      setSelectedDayIndex(0);
    }
  }, [weekOffset]);

  // Scroll to current hour function
  const scrollToCurrentHour = () => {
    const currentHour = new Date().getHours();
    const scrollToHour = currentHour >= 7 && currentHour <= 23 ? currentHour : 7;
    
    const hourElement = document.getElementById(`mobile-hour-${scrollToHour}`);
    if (hourElement && scrollContainerRef.current) {
      const containerRect = scrollContainerRef.current.getBoundingClientRect();
      const elementRect = hourElement.getBoundingClientRect();
      const offset = elementRect.top - containerRect.top + scrollContainerRef.current.scrollTop;
      
      scrollContainerRef.current.scrollTo({ 
        top: offset - 20,
        behavior: 'smooth' 
      });
    }
  };

  // Handle go to today - set week to 0 and day to today
  const handleGoToToday = () => {
    if (weekOffset !== 0) {
      onGoToToday(); // This sets weekOffset to 0
    }
    setSelectedDayIndex(new Date().getDay());
    // Scroll after state updates
    setTimeout(scrollToCurrentHour, 100);
  };

  // Handle selecting a day - if it's today, scroll to current hour
  const handleSelectDay = (index: number) => {
    setSelectedDayIndex(index);
    const isSelectingToday = weekOffset === 0 && index === new Date().getDay();
    if (isSelectingToday) {
      setTimeout(scrollToCurrentHour, 100);
    }
  };

  // Auto-scroll to current hour on mount
  useEffect(() => {
    const currentHour = new Date().getHours();
    const scrollToHour = currentHour >= 7 && currentHour <= 23 ? currentHour : 7;
    
    const timer = setTimeout(() => {
      const hourElement = document.getElementById(`mobile-hour-${scrollToHour}`);
      if (hourElement && scrollContainerRef.current) {
        const containerRect = scrollContainerRef.current.getBoundingClientRect();
        const elementRect = hourElement.getBoundingClientRect();
        const offset = elementRect.top - containerRect.top + scrollContainerRef.current.scrollTop;
        
        scrollContainerRef.current.scrollTo({ 
          top: offset - 80,
          behavior: 'smooth' 
        });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [selectedDayIndex]);

  const navigateDay = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      if (selectedDayIndex > 0) {
        setSelectedDayIndex(prev => prev - 1);
      } else {
        // Go to previous week, Saturday
        onChangeWeek(-1);
        setSelectedDayIndex(6);
      }
    } else {
      if (selectedDayIndex < 6) {
        setSelectedDayIndex(prev => prev + 1);
      } else {
        // Go to next week, Sunday
        onChangeWeek(1);
        setSelectedDayIndex(0);
      }
    }
  };

  const getTasksForDayAndHour = (dayKeyEn: string, hour: number): (WeeklyTask | any)[] => {
    const regularTasks = weeklyTasks.filter(task => {
      const dayMatches = normalizeDay(task.scheduled_day) === dayKeyEn;
      const hourMatches = task.scheduled_hour === hour;
      return dayMatches && hourMatches && selectedUsers.includes(task.user_name);
    });

    const googleEventsForSlot = googleEvents.filter((event: any) => {
      if (!event.start?.dateTime) return false;
      
      const eventStart = new Date(event.start.dateTime);
      const eventDayKey = getEnglishDayKey(eventStart);
      const eventHour = eventStart.getHours();
      
      const user = USERS.find(u => u.id === event.user_name);
      const isUserSelected = user && selectedUsers.includes(user.id);
      
      return eventDayKey === dayKeyEn && 
             eventHour === hour &&
             isUserSelected;
    });

    return [...regularTasks, ...googleEventsForSlot];
  };

  const getUnscheduledTasks = (): WeeklyTask[] => {
    return weeklyTasks.filter(task => {
      const hasNoDay = !task.scheduled_day || task.scheduled_day.trim() === '';
      return hasNoDay && selectedUsers.includes(task.user_name);
    }).sort((a, b) => {
      if (a.isCompleted !== b.isCompleted) {
        return a.isCompleted ? 1 : -1;
      }
      return 0;
    });
  };

  const unscheduledTasks = getUnscheduledTasks();
  const dayKeyEn = getEnglishDayKey(selectedDay);

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col" dir="rtl">
      <MobileCalendarHeader
        currentTime={currentTime}
        selectedDay={selectedDay}
        selectedDayIndex={selectedDayIndex}
        weekOffset={weekOffset}
        weekDays={weekDays}
        selectedUsers={selectedUsers}
        matanBalance={matanBalance}
        isUserAdmin={isUserAdmin}
        onNavigateDay={navigateDay}
        onSelectDay={handleSelectDay}
        onGoToToday={handleGoToToday}
        onToggleUser={onToggleUser}
        onOpenUserSettings={onOpenUserSettings}
        onOpenManagement={onOpenManagement}
        onOpenAddEvent={onOpenAddEvent}
        onOpenCoinHistory={onOpenCoinHistory}
        onTransferCoins={onTransferCoins}
        onNavigateToTasks={onNavigateToTasks}
        onGoToDate={onGoToDate}
        transferCoinsPending={transferCoinsPending}
      />

      <MobileDayView
        ref={scrollContainerRef}
        hours={HOURS}
        dayKeyEn={dayKeyEn}
        selectedDay={selectedDay}
        weekOffset={weekOffset}
        getTasksForDayAndHour={getTasksForDayAndHour}
        onEditTask={onEditTask}
        users={USERS}
        hideEmptyHours={true}
        onSwipe={navigateDay}
      />

      <UnscheduledTasksDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        tasks={unscheduledTasks}
        weekOffset={weekOffset}
        onEditTask={onEditTask}
        users={USERS}
      />
    </div>
  );
}
